import {BackendResponse} from '@common/http/backend-response/backend-response';
import {Playlist} from '@app/web-player/playlists/playlist';
import {UseFormReturn} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {apiClient, queryClient} from '@common/http/query-client';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import {Genre} from '@app/web-player/genres/genre';
import {Tag} from '@common/tags/tag';

interface Response extends BackendResponse {
  playlist: Playlist;
}

export type AutoScheduleMode = 'interval' | 'cron';
export type AutoSchedulePattern =
  | 'minutely'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'custom';

const DEFAULT_SCHEDULE_WEEKDAY = '1';
const DEFAULT_SCHEDULE_DAY_OF_MONTH = '1';
const DEFAULT_SCHEDULE_MONTH_OF_YEAR = '1';
const DEFAULT_SCHEDULE_TIME = '00:00';
const VALID_WEEKDAYS = new Set(['0', '1', '2', '3', '4', '5', '6']);

export interface CreatePlaylistPayload {
  name: string;
  public: boolean;
  collaborative: boolean;
  image?: string | null;
  description?: string | null;
  owner_id?: number | string | null;
  is_automatic?: boolean;
  auto_update_interval_days?: number | string | null;
  auto_schedule_cron?: string | null;
  auto_create_new_playlist?: boolean;
  auto_playlist_series_name?: string | null;
  auto_schedule_mode?: AutoScheduleMode;
  auto_schedule_pattern?: AutoSchedulePattern;
  auto_schedule_every?: number | string | null;
  auto_schedule_weekday?: number | string | null;
  auto_schedule_day_of_month?: number | string | null;
  auto_schedule_month_of_year?: number | string | null;
  auto_schedule_time?: string | null;
  auto_filter_mode?: 'or' | 'and' | null;
  auto_max_tracks_per_artist?: number | string | null;
  auto_max_tracks?: number | string | null;
  auto_boost_new_releases?: boolean;
  auto_rotation_days?: number | string | null;
  auto_popularity_ratio?: number | string | null;
  auto_title_pattern?: string | null;
  auto_counter?: number | string | null;
  genres?: Array<Genre | {id?: number | string | null} | number | string>;
  tags?: Array<Tag | {id?: number | string | null} | number | string>;
}

export function buildAutoScheduleCron(options: {
  pattern: Exclude<AutoSchedulePattern, 'custom'>;
  every: number;
  weekday: string;
  dayOfMonth: number;
  monthOfYear: number;
  time: string;
}): string {
  const [hour, minute] = parseTimeParts(options.time);
  const every = Math.max(1, Math.floor(options.every));

  if (options.pattern === 'minutely') {
    const minuteStep = Math.min(59, every);
    return minuteStep <= 1 ? '* * * * *' : `*/${minuteStep} * * * *`;
  }

  if (options.pattern === 'hourly') {
    const hourStep = Math.min(23, every);
    const hourPart = hourStep <= 1 ? '*' : `*/${hourStep}`;
    return `${minute} ${hourPart} * * *`;
  }

  if (options.pattern === 'daily') {
    return `${minute} ${hour} * * *`;
  }

  if (options.pattern === 'weekly') {
    const weekday = normalizeScheduleWeekday(options.weekday);
    return `${minute} ${hour} * * ${weekday}`;
  }

  if (options.pattern === 'monthly') {
    const dayOfMonth = normalizeDayOfMonth(options.dayOfMonth);
    const monthStep = every > 1 ? `*/${every}` : '*';
    return `${minute} ${hour} ${dayOfMonth} ${monthStep} *`;
  }

  const dayOfMonth = normalizeDayOfMonth(options.dayOfMonth);
  const monthOfYear = normalizeMonthOfYear(options.monthOfYear);
  return `${minute} ${hour} ${dayOfMonth} ${monthOfYear} *`;
}

export function getScheduleBuilderDefaults(options?: {
  auto_schedule_cron?: string | null;
  auto_update_interval_days?: number | null;
}): {
  auto_schedule_mode: AutoScheduleMode;
  auto_schedule_pattern: AutoSchedulePattern;
  auto_schedule_every: string;
  auto_schedule_weekday: string;
  auto_schedule_day_of_month: string;
  auto_schedule_month_of_year: string;
  auto_schedule_time: string;
} {
  const expression = String(options?.auto_schedule_cron ?? '').trim();
  const intervalDays = normalizePositiveInteger(
    options?.auto_update_interval_days,
    1,
  );

  if (!expression) {
    return {
      auto_schedule_mode: 'interval',
      auto_schedule_pattern: 'weekly',
      auto_schedule_every: '1',
      auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
      auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
      auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
      auto_schedule_time: DEFAULT_SCHEDULE_TIME,
    };
  }

  const parts = expression.split(/\s+/);
  if (parts.length < 5) {
    return {
      auto_schedule_mode: 'cron',
      auto_schedule_pattern: 'custom',
      auto_schedule_every: '1',
      auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
      auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
      auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
      auto_schedule_time: DEFAULT_SCHEDULE_TIME,
    };
  }

  const minute = parts[0] ?? '*';
  const hour = parts[1] ?? '*';
  const dayOfMonth = parts[2] ?? '*';
  const month = parts[3] ?? '*';
  const dayOfWeek = parts[4] ?? '*';
  const time = normalizeScheduleTime(`${hour}:${minute}`);

  if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
    if (minute === '*' && hour === '*') {
      return {
        auto_schedule_mode: 'cron',
        auto_schedule_pattern: 'minutely',
        auto_schedule_every: '1',
        auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
        auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
        auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
        auto_schedule_time: DEFAULT_SCHEDULE_TIME,
      };
    }

    const minuteStep = parseCronStep(minute, 1, 59);
    if (minuteStep !== null && hour === '*') {
      return {
        auto_schedule_mode: 'cron',
        auto_schedule_pattern: 'minutely',
        auto_schedule_every: String(minuteStep),
        auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
        auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
        auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
        auto_schedule_time: DEFAULT_SCHEDULE_TIME,
      };
    }

    const minuteInHour = parseBoundedInteger(minute, 0, 59);
    const hourStep = parseCronStep(hour, 1, 23);
    if (minuteInHour !== null && (hour === '*' || hourStep !== null)) {
      return {
        auto_schedule_mode: 'cron',
        auto_schedule_pattern: 'hourly',
        auto_schedule_every: String(hour === '*' ? 1 : hourStep ?? 1),
        auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
        auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
        auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
        auto_schedule_time: normalizeScheduleTime(`00:${minuteInHour}`),
      };
    }
  }

  const weekday = extractSingleCronWeekday(dayOfWeek);
  if (weekday && dayOfMonth === '*' && month === '*') {
    const everyWeeks =
      intervalDays && intervalDays >= 7 && intervalDays % 7 === 0
        ? Math.max(1, Math.round(intervalDays / 7))
        : 1;

    return {
      auto_schedule_mode: 'cron',
      auto_schedule_pattern: 'weekly',
      auto_schedule_every: String(everyWeeks),
      auto_schedule_weekday: weekday,
      auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
      auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
      auto_schedule_time: time,
    };
  }

  if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
    return {
      auto_schedule_mode: 'cron',
      auto_schedule_pattern: 'daily',
      auto_schedule_every: String(intervalDays ?? 1),
      auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
      auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
      auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
      auto_schedule_time: time,
    };
  }

  const parsedDay = parseBoundedInteger(dayOfMonth, 1, 31);
  const parsedMonth = parseBoundedInteger(month, 1, 12);
  const parsedMonthStep = parseCronStep(month, 1, 12);

  if (
    dayOfWeek === '*' &&
    parsedDay !== null &&
    (month === '*' || parsedMonthStep !== null)
  ) {
    return {
      auto_schedule_mode: 'cron',
      auto_schedule_pattern: 'monthly',
      auto_schedule_every: String(parsedMonthStep ?? 1),
      auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
      auto_schedule_day_of_month: String(parsedDay),
      auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
      auto_schedule_time: time,
    };
  }

  if (dayOfWeek === '*' && parsedDay !== null && parsedMonth !== null) {
    const everyYears =
      intervalDays && intervalDays >= 365
        ? Math.max(1, Math.round(intervalDays / 365))
        : 1;

    return {
      auto_schedule_mode: 'cron',
      auto_schedule_pattern: 'yearly',
      auto_schedule_every: String(everyYears),
      auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
      auto_schedule_day_of_month: String(parsedDay),
      auto_schedule_month_of_year: String(parsedMonth),
      auto_schedule_time: time,
    };
  }

  return {
    auto_schedule_mode: 'cron',
    auto_schedule_pattern: 'custom',
    auto_schedule_every: '1',
    auto_schedule_weekday: DEFAULT_SCHEDULE_WEEKDAY,
    auto_schedule_day_of_month: DEFAULT_SCHEDULE_DAY_OF_MONTH,
    auto_schedule_month_of_year: DEFAULT_SCHEDULE_MONTH_OF_YEAR,
    auto_schedule_time: DEFAULT_SCHEDULE_TIME,
  };
}

export function useCreatePlaylist(form: UseFormReturn<CreatePlaylistPayload>) {
  return useMutation({
    mutationFn: (props: CreatePlaylistPayload) => createPlaylist(props),
    onSuccess: () => {
      toast(message('Playlist created'));
      queryClient.invalidateQueries({queryKey: ['playlists']});
    },
    onError: r => onFormQueryError(r, form),
  });
}

function createPlaylist(payload: CreatePlaylistPayload): Promise<Response> {
  return apiClient
    .post('playlists', normalizePlaylistPayload(payload))
    .then(r => r.data);
}

export function normalizePlaylistPayload(
  payload: Partial<CreatePlaylistPayload>,
) {
  const normalized: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) {
      normalized[key] = value;
    }
  });

  if (
    Object.prototype.hasOwnProperty.call(payload, 'genres') &&
    Array.isArray(payload.genres)
  ) {
    normalized.genres = payload.genres
      .map(genre => {
        if (typeof genre === 'number' || typeof genre === 'string') {
          return Number(genre);
        }
        const id = (genre as {id?: number | string | null})?.id;
        return id == null ? null : Number(id);
      })
      .filter(
        (id): id is number =>
          typeof id === 'number' && Number.isInteger(id) && id > 0,
      );
  } else if (Object.prototype.hasOwnProperty.call(payload, 'genres')) {
    delete normalized.genres;
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'tags') &&
    Array.isArray(payload.tags)
  ) {
    normalized.tags = payload.tags
      .map(tag => {
        if (typeof tag === 'number' || typeof tag === 'string') {
          return Number(tag);
        }
        const id = (tag as {id?: number | string | null})?.id;
        return id == null ? null : Number(id);
      })
      .filter(
        (id): id is number =>
          typeof id === 'number' && Number.isInteger(id) && id > 0,
      );
  } else if (Object.prototype.hasOwnProperty.call(payload, 'tags')) {
    delete normalized.tags;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'owner_id')) {
    if (payload.owner_id === '' || payload.owner_id == null) {
      delete normalized.owner_id;
    } else {
      normalized.owner_id = Number(payload.owner_id);
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'auto_update_interval_days')
  ) {
    if (
      payload.auto_update_interval_days === '' ||
      payload.auto_update_interval_days == null
    ) {
      normalized.auto_update_interval_days = null;
    } else {
      normalized.auto_update_interval_days = Number(
        payload.auto_update_interval_days,
      );
    }
  }

  const scheduleMode = normalizeScheduleMode(
    payload.auto_schedule_mode,
    payload.auto_schedule_cron,
  );
  const schedulePattern = normalizeSchedulePattern(
    payload.auto_schedule_pattern,
  );
  const scheduleEvery = normalizePositiveInteger(
    payload.auto_schedule_every,
    1,
  );
  const scheduleWeekday = normalizeScheduleWeekday(
    payload.auto_schedule_weekday,
  );
  const scheduleDayOfMonth = normalizeDayOfMonth(
    payload.auto_schedule_day_of_month,
  );
  const scheduleMonthOfYear = normalizeMonthOfYear(
    payload.auto_schedule_month_of_year,
  );
  const scheduleTime = normalizeScheduleTime(payload.auto_schedule_time);

  if (scheduleMode === 'cron') {
    if (schedulePattern === 'custom') {
      const cron = String(payload.auto_schedule_cron ?? '').trim();
      normalized.auto_schedule_cron = cron !== '' ? cron : null;
    } else {
      normalized.auto_schedule_cron = buildAutoScheduleCron({
        pattern: schedulePattern,
        every: scheduleEvery,
        weekday: scheduleWeekday,
        dayOfMonth: scheduleDayOfMonth,
        monthOfYear: scheduleMonthOfYear,
        time: scheduleTime,
      });

      normalized.auto_update_interval_days = deriveScheduleMinIntervalDays(
        schedulePattern,
        scheduleEvery,
      );
    }
  } else {
    normalized.auto_schedule_cron = null;
  }

  delete normalized.auto_schedule_mode;
  delete normalized.auto_schedule_pattern;
  delete normalized.auto_schedule_every;
  delete normalized.auto_schedule_weekday;
  delete normalized.auto_schedule_day_of_month;
  delete normalized.auto_schedule_month_of_year;
  delete normalized.auto_schedule_time;

  if (
    Object.prototype.hasOwnProperty.call(payload, 'auto_create_new_playlist')
  ) {
    normalized.auto_create_new_playlist = Boolean(
      payload.auto_create_new_playlist,
    );
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'auto_playlist_series_name')
  ) {
    const seriesName = String(payload.auto_playlist_series_name ?? '').trim();
    normalized.auto_playlist_series_name =
      seriesName !== '' ? seriesName : null;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_filter_mode')) {
    const mode = String(payload.auto_filter_mode ?? '')
      .trim()
      .toLowerCase();
    normalized.auto_filter_mode = mode === 'and' ? 'and' : 'or';
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'auto_max_tracks_per_artist')
  ) {
    if (
      payload.auto_max_tracks_per_artist === '' ||
      payload.auto_max_tracks_per_artist == null
    ) {
      normalized.auto_max_tracks_per_artist = null;
    } else {
      normalized.auto_max_tracks_per_artist = Math.max(
        1,
        Number(payload.auto_max_tracks_per_artist),
      );
    }
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_max_tracks')) {
    if (payload.auto_max_tracks === '' || payload.auto_max_tracks == null) {
      normalized.auto_max_tracks = null;
    } else {
      normalized.auto_max_tracks = Math.max(1, Number(payload.auto_max_tracks));
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(payload, 'auto_boost_new_releases')
  ) {
    normalized.auto_boost_new_releases = Boolean(
      payload.auto_boost_new_releases,
    );
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_rotation_days')) {
    if (
      payload.auto_rotation_days === '' ||
      payload.auto_rotation_days == null
    ) {
      normalized.auto_rotation_days = null;
    } else {
      normalized.auto_rotation_days = Math.max(
        1,
        Number(payload.auto_rotation_days),
      );
    }
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_popularity_ratio')) {
    if (
      payload.auto_popularity_ratio === '' ||
      payload.auto_popularity_ratio == null
    ) {
      normalized.auto_popularity_ratio = 50;
    } else {
      normalized.auto_popularity_ratio = Math.min(
        100,
        Math.max(0, Number(payload.auto_popularity_ratio)),
      );
    }
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_title_pattern')) {
    const pattern = (payload.auto_title_pattern ?? '').trim();
    normalized.auto_title_pattern = pattern !== '' ? pattern : null;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'auto_counter')) {
    if (payload.auto_counter === '' || payload.auto_counter == null) {
      normalized.auto_counter = 0;
    } else {
      normalized.auto_counter = Math.max(0, Number(payload.auto_counter));
    }
  }

  return normalized;
}

function normalizeScheduleMode(
  value: unknown,
  cronExpression?: string | null,
): AutoScheduleMode {
  if (value === 'cron') return 'cron';
  if (value === 'interval') return 'interval';

  return String(cronExpression ?? '').trim() !== '' ? 'cron' : 'interval';
}

function normalizeSchedulePattern(value: unknown): AutoSchedulePattern {
  if (value === 'minutely') return 'minutely';
  if (value === 'hourly') return 'hourly';
  if (value === 'daily') return 'daily';
  if (value === 'weekly') return 'weekly';
  if (value === 'monthly') return 'monthly';
  if (value === 'yearly') return 'yearly';
  if (value === 'custom') return 'custom';
  return 'weekly';
}

function normalizeScheduleWeekday(value: unknown): string {
  const weekday = String(value ?? DEFAULT_SCHEDULE_WEEKDAY).trim();
  return VALID_WEEKDAYS.has(weekday) ? weekday : DEFAULT_SCHEDULE_WEEKDAY;
}

function normalizeDayOfMonth(value: unknown): number {
  return parseBoundedInteger(value, 1, 31) ?? 1;
}

function normalizeMonthOfYear(value: unknown): number {
  return parseBoundedInteger(value, 1, 12) ?? 1;
}

function normalizeScheduleTime(value: unknown): string {
  const candidate = String(value ?? DEFAULT_SCHEDULE_TIME).trim();
  const match = candidate.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) {
    return DEFAULT_SCHEDULE_TIME;
  }

  const hour = parseBoundedInteger(match[1], 0, 23);
  const minute = parseBoundedInteger(match[2], 0, 59);
  if (hour === null || minute === null) {
    return DEFAULT_SCHEDULE_TIME;
  }

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function parseTimeParts(value: string): [string, string] {
  const normalized = normalizeScheduleTime(value);
  const [hour, minute] = normalized.split(':');
  return [hour || '00', minute || '00'];
}

function deriveScheduleMinIntervalDays(
  pattern: Exclude<AutoSchedulePattern, 'custom'>,
  every: number,
): number | null {
  if (every <= 1) {
    return null;
  }

  if (pattern === 'daily') {
    return every;
  }

  if (pattern === 'weekly') {
    return every * 7;
  }

  if (pattern === 'yearly') {
    return every * 365;
  }

  if (pattern === 'minutely' || pattern === 'hourly') {
    return null;
  }

  return null;
}

function normalizePositiveInteger(value: unknown, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.max(1, Math.floor(parsed));
}

function parseBoundedInteger(
  value: unknown,
  min: number,
  max: number,
): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  const floored = Math.floor(parsed);
  if (floored < min || floored > max) {
    return null;
  }

  return floored;
}

function parseCronStep(value: string, min: number, max: number): number | null {
  const match = String(value ?? '')
    .trim()
    .match(/^\*\/(\d{1,3})$/);
  if (!match) {
    return null;
  }

  const parsed = parseBoundedInteger(match[1], min, max);
  return parsed ?? null;
}

function extractSingleCronWeekday(value: string): string | null {
  const clean = String(value ?? '').trim();
  if (!clean) return null;

  if (clean.includes(',') || clean.includes('/') || clean.includes('-')) {
    return null;
  }

  if (!VALID_WEEKDAYS.has(clean)) {
    return null;
  }

  return clean;
}
